/**
 * Global singleton + hook for handling the browser PWA install prompt.
 *
 * Why a singleton? `beforeinstallprompt` fires exactly once per page load and
 * can only be deferred/consumed once. Multiple components (Header button,
 * floating InstallPrompt banner) need to coordinate on the same event, so we
 * stash it on `window.__aiglPwaInstallEvent` and expose it via a hook with a
 * tiny pub/sub.
 */
import { useEffect, useState } from "react";

const LISTENERS = new Set();
const STATE = {
  deferredEvent: null,
  installed: false,
};

function emit() {
  LISTENERS.forEach((fn) => fn({ ...STATE }));
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

let bootstrapped = false;
function bootstrap() {
  if (bootstrapped || typeof window === "undefined") return;
  bootstrapped = true;
  STATE.installed = isStandalone();
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    STATE.deferredEvent = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    STATE.deferredEvent = null;
    STATE.installed = true;
    emit();
  });
}

/** Returns { canInstall, isInstalled, isIOS, install, deferredEvent } */
export function usePwaInstall() {
  bootstrap();
  const [state, setState] = useState({ ...STATE });

  useEffect(() => {
    LISTENERS.add(setState);
    setState({ ...STATE });
    return () => {
      LISTENERS.delete(setState);
    };
  }, []);

  const install = async () => {
    const evt = STATE.deferredEvent;
    if (!evt) return { outcome: "unsupported" };
    try {
      await evt.prompt();
      const { outcome } = await evt.userChoice;
      STATE.deferredEvent = null;
      emit();
      return { outcome };
    } catch (e) {
      return { outcome: "error", error: e };
    }
  };

  return {
    canInstall: Boolean(state.deferredEvent),
    isInstalled: state.installed,
    isIOS: isIOSSafari(),
    install,
  };
}
